import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';
import CustomInput from '../../utils/redux-forms-custom-fields/CustomInput';
import CustomInputCheckbox from '../../utils/redux-forms-custom-fields/CustomInputCheckbox';
import CustomDropDownGeneric from '../../utils/redux-forms-custom-fields/CustomDropDownGeneric';
import CustomDateTimePicker from '../../utils/redux-forms-custom-fields/CustomDateTimePicker';
import CustomDropDown from '../../utils/redux-forms-custom-fields/CustomDropDown';
import { simpleNotification } from '../../utils/notifications';
import DeleteUserConfirmationDialog from '../dialog/DeleteUserConfirmationDialog';
import { adminDeleteUserSafely } from '../../utils/apiv2';
import {
  getCorrectTwitchAverages,
  getCorrectYoutubeAverages,
} from '../../utils/functions';

const dateStoreFormat = 'YYYY-MM-DD';
const dateDisplayFormat = 'MM-DD-YYYY';

class AdminUserForm extends React.Component {
  constructor(props) {
    super(props);
    this.props = props;
    this.state = {
      disconnectedPlatforms: new Set(),
      deleteDialogOpen: false,
      isDeleting: false,
    };

    // Platform configuration
    this.platforms = {
      twitch: { label: 'Twitch', displayName: 'Twitch Username' },
      twitter: { label: 'Twitter', displayName: 'Twitter Username' },
      steam: { label: 'Steam', displayName: 'Steam Username' },
      youtube: { label: 'YouTube', displayName: 'YouTube Username' },
    };
  }

  onDisconnectPlatform = (platformName) => {
    const { disconnectedPlatforms } = this.state;
    disconnectedPlatforms.add(platformName);
    this.setState({ disconnectedPlatforms });
    simpleNotification({
      level: 'warning',
      title: `Disconnect ${this.platforms[platformName].label}`,
      message: `Save button must be clicked in order to disconnect ${this.platforms[platformName].label} successfully`,
    });
  }

  onOpenDeleteDialog = () => {
    this.setState({
      deleteDialogOpen: true,
    });
  }
  onCloseDeleteDialog = () => {
    this.setState({
      deleteDialogOpen: false,
    });
  }
  onConfirmDeleteUser = () => {
    const { currUserId, onDeleteSuccess } = this.props;
    const { deleteDialogOpen } = this.state;
    if (!deleteDialogOpen || !currUserId) {
      return;
    }
    this.setState({ isDeleting: true });
    adminDeleteUserSafely(currUserId)
      .then((response) => {
        const isDeleted = response?.data?.code === 'USER_DELETED_SAFELY';
        if (!isDeleted) {
          this.setState({ isDeleting: false });
          return;
        }

        this.setState({
          deleteDialogOpen: false,
          isDeleting: false,
        });
        // Call the success callback if provided
        if (onDeleteSuccess) {
          onDeleteSuccess();
        }
      })
      .catch((error) => {
        this.setState({ isDeleting: false });
        console.error('Delete user error:', error);
      });
  }
  submit = (info) => {
    const { disconnectedPlatforms } = this.state;
    this.props.onSubmit(info, disconnectedPlatforms);
  }

  renderPlatformSection = (platformName) => {
    const { disconnectedPlatforms } = this.state;
    const { adminUserForm } = this.props;
    const platformConfig = this.platforms[platformName];

    const isConnected = !disconnectedPlatforms.has(platformName);
    const hasUsername = adminUserForm?.values?.connectedAccounts?.[platformName]?.username;

    if (!isConnected || !hasUsername) return null;

    // Special handling for Twitch with AVGViewers
    if (platformName === 'twitch') {
      const AVGViewers = getCorrectTwitchAverages(adminUserForm.values, true).correctAverage;
      return (
        <Fragment key={platformName}>
          <div className="AUserForm__disconnectPlatform--container" onClick={() => this.onDisconnectPlatform(platformName)} aria-hidden>
            <div className="AUserForm__disconnectPlatform">Disconnect {platformConfig.label} Account</div>
          </div>
          <div className="input-container">
            <label>{platformConfig.displayName}</label>
            <div className="input-wrapper">
              <input type="text" value={hasUsername} className="touched ok not-empty" disabled />
            </div>
          </div>
          <Fragment>
            <div className="input-container">
              <label>Actual Average Twitch Viewers</label>
              <div className="input-wrapper">
                <input autoComplete="off" type="text" className="ok empty" disabled value={Math.round(AVGViewers)} />
              </div>
            </div>
            <Field
              name="freezeTwitchViewers.isFrozen"
              id="freezeTwitchViewers.isFrozen"
              customLabel={((<label>Freeze Twitch Viewers</label>))}
              component={CustomInputCheckbox}
            />
            <Field
              type="text"
              name="twitchAVGOverride"
              id="twitchAVGOverride"
              placeholder="0"
              customLabel={((<label>Twitch Average Viewer Override</label>))}
              component={CustomInput}
            />
          </Fragment>
        </Fragment>
      );
    }

    // Special handling for YouTube with avgPlayCount
    if (platformName === 'youtube') {
      console.log('adminUserForm.values', adminUserForm.values);
      const { correctAverage: youtubeCorrectAverage } = getCorrectYoutubeAverages(adminUserForm.values || {});
      const avgPlayCount = adminUserForm.values?.connectedAccounts?.youtube?.avgPlayCount || 0;

      return (
        <Fragment key={platformName}>
          <div className="AUserForm__disconnectPlatform--container" onClick={() => this.onDisconnectPlatform(platformName)} aria-hidden>
            <div className="AUserForm__disconnectPlatform">Disconnect {platformConfig.label} Account</div>
          </div>
          <div className="input-container">
            <label>{platformConfig.displayName}</label>
            <div className="input-wrapper">
              <input type="text" value={hasUsername} className="touched ok not-empty" disabled />
            </div>
          </div>
          <Fragment>
            <div className="input-container">
              <label>Actual Average YouTube Play Count</label>
              <div className="input-wrapper">
                <input autoComplete="off" type="text" className="ok empty" disabled value={Math.round(avgPlayCount)} />
              </div>
            </div>
            <div className="input-container">
              <label>Current Average YouTube Play Count (Used in Calculations)</label>
              <div className="input-wrapper">
                <input autoComplete="off" type="text" className="ok empty" disabled value={Math.round(youtubeCorrectAverage || 0)} />
              </div>
            </div>
            <Field
              name="freezeYoutubeViews.isFrozen"
              id="freezeYoutubeViews.isFrozen"
              customLabel={((<label>Freeze YouTube Views</label>))}
              component={CustomInputCheckbox}
            />
            <Field
              type="text"
              name="youtubeAVGOverride"
              id="youtubeAVGOverride"
              placeholder="0"
              customLabel={((<label>YouTube Average Play Count Override</label>))}
              component={CustomInput}
            />
          </Fragment>
        </Fragment>
      );
    }

    // Standard platform section
    return (
      <Fragment key={platformName}>
        <div className="AUserForm__disconnectPlatform--container" onClick={() => this.onDisconnectPlatform(platformName)} aria-hidden>
          <div className="AUserForm__disconnectPlatform">Disconnect {platformConfig.label} Account</div>
        </div>
        <div className="input-container">
          <label>{platformConfig.displayName}</label>
          <div className="input-wrapper">
            <input type="text" value={hasUsername} className="touched ok not-empty" disabled />
          </div>
        </div>
      </Fragment>
    );
  }

  render() {
    const { handleSubmit, adminUserForm } = this.props;
    const {
      deleteDialogOpen,
      isDeleting,
    } = this.state;
    let recoveryToken = '';
    try {
      recoveryToken = (adminUserForm && adminUserForm.values) ? adminUserForm.values.recoveryToken : '';
    } catch (err) {
      console.log(err);
    }
    const recoveryLink = (recoveryToken !== '') ? `${window.location.origin}/recover?recovery-token=${recoveryToken}` : '';

    return (
      <form className="form-container admin-user" autoComplete="off" onSubmit={handleSubmit(this.submit.bind(this))}>
        <Fragment>
          <div className="input-container">
            <label>User ID</label>
            <div className="input-wrapper">
              <input autoComplete="off" id="recoveryLink" name="recoveryLink" type="text" placeholder="" className="ok empty" value={this.props.currUserId} />
            </div>
          </div>
          <Field
            type="text"
            name="firstName"
            id="firstName"
            placeholder="First Name"
            customLabel={(<label>First Name</label>)}
            component={CustomInput}
          />
          <Field
            type="text"
            name="lastName"
            id="lastName"
            placeholder="Last Name"
            customLabel={(<label>Last Name</label>)}
            component={CustomInput}
          />
          <Field
            type="text"
            name="username"
            id="username"
            placeholder="Username"
            customLabel={((<label>Username</label>))}
            component={CustomInput}
          />
          <Field
            type="text"
            name="email"
            id="email"
            placeholder="Email"
            customLabel={(<label>Email</label>)}
            component={CustomInput}
          />
          <Field
            name="role"
            id="role"
            placeholder="Select Role"
            customLabel={((<label>Role</label>))}
            component={CustomDropDownGeneric}
            options={['user', 'publisher', 'helper', 'brand']}
          />
          {adminUserForm && adminUserForm.values && adminUserForm.values.role === 'brand' ? (
            <Field
              type="text"
              name="company"
              id="company"
              placeholder="Company Name"
              customLabel={((<label>Company Name</label>))}
              component={CustomInput}
            />
          ) : null}
          <Field
            name="status"
            id="status"
            placeholder="Select Status"
            customLabel={((<label>Status</label>))}
            component={CustomDropDownGeneric}
            options={['active', 'on wait list', 'deleted']}
          />
          <Field
            type="text"
            name="birthDate"
            id="birthDate"
            placeholder="Birth Date"
            dateStoreFormat={dateStoreFormat}
            dateDisplayFormat={dateDisplayFormat}
            customLabel={(<label className="datepicker-label">Birth Date</label>)}
            component={CustomDateTimePicker}
          />
          <Field
            type="text"
            name="country"
            id="country"
            customLabel={(<label>Country</label>)}
            component={CustomDropDown}
          />
          <Field
            type="text"
            name="language"
            id="language"
            customLabel={(<label>Language</label>)}
            component={CustomInput}
          />
          {/* <Field
            type="text"
            name="twitter"
            id="twitter"
            customLabel={(<label>Twitter</label>)}
            component={CustomInput}
          /> */}
          <Field
            type="text"
            name="discord"
            id="discord"
            customLabel={(<label>Discord</label>)}
            component={CustomInput}
          />
          <Field
            name="blacklist"
            id="blacklist"
            customLabel={((<label>Blacklist</label>))}
            component={CustomInputCheckbox}
          />
          <Field
            name="offPlatform"
            id="offPlatform"
            customLabel={((<label>User is Off Platform</label>))}
            component={CustomInputCheckbox}
          />
          <Field
            type="textarea"
            name="notes"
            id="notes"
            customLabel={(<label>Notes</label>)}
            className="extra-big-text-area"
            component={CustomInput}
          />
        </Fragment>

        {/* { (adminUserForm && adminUserForm.values &&
          adminUserForm.values.firstName &&
          adminUserForm.values.lastName &&
          adminUserForm.values.username &&
          adminUserForm.values.email) && (
          <Fragment>
            <div className="input-container">
              <label>First Name</label>
              <div className="input-wrapper">
                <input type="text" value={`${adminUserForm.values.firstName}`} className="touched ok not-empty" disabled />
              </div>
            </div>
            <div className="input-container">
              <label>Last Name</label>
              <div className="input-wrapper">
                <input type="text" value={`${adminUserForm.values.lastName}`} className="touched ok not-empty" disabled />
              </div>
            </div>
            <div className="input-container">
              <label>Username</label>
              <div className="input-wrapper">
                <input type="text" value={`${adminUserForm.values.username}`} className="touched ok not-empty" disabled />
              </div>
            </div>
            <div className="input-container">
              <label>Email</label>
              <div className="input-wrapper">
                <input type="text" value={`${adminUserForm.values.email}`} className="touched ok not-empty" disabled />
              </div>
            </div>

          </Fragment>
        )
      } */}
        {Object.keys(this.platforms).map(platformName => this.renderPlatformSection(platformName))}

        <div className="input-container">
          <label>Password Recovery Link</label>
          <div className="input-wrapper" style={{ display: 'flex', gap: '8px' }}>
            <input autoComplete="off" id="recoveryLink" name="recoveryLink" type="text" placeholder="" className="ok empty" disabled value={recoveryLink} style={{ flex: 1 }} />
            <button
              type="button"
              className="AUserForm__copy--btn"
              disabled={!recoveryLink}
              onClick={() => {
                if (!recoveryLink) return;
                navigator.clipboard.writeText(recoveryLink).then(() => {
                  simpleNotification({
                    level: 'success',
                    title: 'Copied',
                    message: 'Password recovery link copied to clipboard.',
                  });
                }).catch(() => {
                  simpleNotification({
                    level: 'error',
                    title: 'Copy Failed',
                    message: 'Could not copy link. Please copy it manually.',
                  });
                });
              }}
            >
              Copy Link
            </button>
          </div>
        </div>

        <div className="form__footer">
          <button className="AUserForm__save--btn">Save</button>
          <button
            type="button"
            className="AUserForm__delete--btn"
            onClick={this.onOpenDeleteDialog}
          >
            Delete User
          </button>
        </div>
        <DeleteUserConfirmationDialog
          open={deleteDialogOpen}
          onClose={this.onCloseDeleteDialog}
          onConfirm={this.onConfirmDeleteUser}
          username={adminUserForm && adminUserForm.values ? adminUserForm.values.username || '' : ''}
          isLoading={isDeleting}
        />
      </form>
    );
  }
}

const validate = (values) => {
  const errors = {};
  if (!values.username || values.username.trim() === '') {
    errors.username = 'This field is required';
  }
  if (!values.email || values.email.trim() === '') {
    errors.email = 'This field is required';
  }
  if (!values.role || values.role.trim() === '') {
    errors.role = 'This field is required';
  }
  if (!values.status || values.status.trim() === '') {
    errors.status = 'This field is required';
  }
  return errors;
};


AdminUserForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
};

export default reduxForm({
  form: 'adminUserForm',
  validate,
})(AdminUserForm);
